export const courses = [
  {
    id: 1,
    title: "Python Programming",
    description: "Master Python from basics to advanced concepts including OOP, data structures, and frameworks.",
    duration: "8-12 weeks",
    level: "Beginner to Advanced",
    price: "$25/hour",
    topics: ["Variables & Data Types", "Control Structures", "Functions", "OOP", "Django/Flask", "Data Analysis"],
    icon: "🐍",
    color: "bg-green-500",
    slug: "python-programming",
    detailedDescription: "Transform from a complete beginner to a confident Python developer. This comprehensive course covers everything from basic syntax to advanced frameworks, preparing you for real-world development projects.",
    curriculum: [
      {
        week: "Weeks 1-2",
        title: "Python Fundamentals",
        topics: ["Variables and data types", "Control structures (if/else, loops)", "Functions and modules", "Error handling", "File I/O operations"]
      },
      {
        week: "Weeks 3-4", 
        title: "Data Structures & Algorithms",
        topics: ["Lists, tuples, dictionaries", "Sets and comprehensions", "Basic algorithms", "Time complexity", "Problem-solving techniques"]
      },
      {
        week: "Weeks 5-6",
        title: "Object-Oriented Programming",
        topics: ["Classes and objects", "Inheritance and polymorphism", "Encapsulation", "Design patterns", "Best practices"]
      },
      {
        week: "Weeks 7-8",
        title: "Web Development",
        topics: ["Django framework", "Flask microframework", "Database integration", "API development", "Deployment basics"]
      },
      {
        week: "Weeks 9-10",
        title: "Data Analysis & Visualization",
        topics: ["NumPy and Pandas", "Matplotlib and Seaborn", "Data cleaning", "Statistical analysis", "Real-world projects"]
      },
      {
        week: "Weeks 11-12",
        title: "Advanced Topics & Portfolio",
        topics: ["Testing and debugging", "Performance optimization", "Package management", "Portfolio projects", "Career preparation"]
      }
    ],
    prerequisites: ["Basic computer skills", "No prior programming experience required", "Willingness to practice regularly"],
    outcomes: [
      "Build complete Python applications from scratch",
      "Understand and implement OOP concepts",
      "Create web applications using Django/Flask",
      "Analyze and visualize data effectively",
      "Write clean, maintainable code",
      "Debug and test your applications",
      "Deploy applications to production"
    ],
    projects: [
      "Personal portfolio website",
      "Data analysis dashboard",
      "Web scraping application",
      "REST API with authentication",
      "Machine learning model deployment"
    ],
    testimonials: [
      {
        name: "Sarah Johnson",
        role: "Data Analyst",
        content: "Duke's Python course transformed my career! I went from knowing nothing to building my first web application in just 8 weeks.",
        rating: 5
      }
    ],
    faq: [
      {
        question: "Do I need any programming experience?",
        answer: "No! This course is designed for complete beginners. We start from the very basics and build up your skills progressively."
      },
      {
        question: "What projects will I build?",
        answer: "You'll build 5 real-world projects including a portfolio website, data analysis dashboard, and a REST API with authentication."
      },
      {
        question: "How much time should I dedicate per week?",
        answer: "We recommend 6-8 hours per week including live sessions, practice, and project work for optimal learning."
      }
    ]
  },
  {
    id: 2,
    title: "React Development",
    description: "Build modern web applications with React, hooks, state management, and best practices.",
    duration: "6-10 weeks",
    level: "Intermediate",
    price: "$30/hour",
    topics: ["Components", "Hooks", "State Management", "Routing", "Testing", "Deployment"],
    icon: "⚛️",
    color: "bg-blue-500",
    slug: "react-development",
    detailedDescription: "Master modern React development and build stunning, interactive web applications. Learn the latest React patterns, hooks, and ecosystem tools used by top companies worldwide.",
    curriculum: [
      {
        week: "Weeks 1-2",
        title: "React Fundamentals",
        topics: ["JSX and components", "Props and state", "Event handling", "Conditional rendering", "Lists and keys"]
      },
      {
        week: "Weeks 3-4",
        title: "React Hooks & Advanced Patterns",
        topics: ["useState and useEffect", "Custom hooks", "useContext", "useReducer", "Performance optimization"]
      },
      {
        week: "Weeks 5-6",
        title: "State Management & Routing",
        topics: ["Redux Toolkit", "React Router", "Context API", "Global state patterns", "Navigation best practices"]
      },
      {
        week: "Weeks 7-8",
        title: "Testing & Quality",
        topics: ["Jest and React Testing Library", "Component testing", "Integration testing", "Mocking", "Test-driven development"]
      },
      {
        week: "Weeks 9-10",
        title: "Production & Deployment",
        topics: ["Build optimization", "Performance monitoring", "Deployment strategies", "CI/CD", "Production debugging"]
      }
    ],
    prerequisites: ["Basic JavaScript knowledge", "HTML/CSS fundamentals", "Understanding of ES6+ features"],
    outcomes: [
      "Build scalable React applications",
      "Master React hooks and modern patterns",
      "Implement effective state management",
      "Write comprehensive tests",
      "Deploy applications to production",
      "Optimize application performance",
      "Work with React ecosystem tools"
    ],
    projects: [
      "E-commerce dashboard",
      "Social media application",
      "Task management system",
      "Real-time chat application",
      "Portfolio website with animations"
    ],
    testimonials: [
      {
        name: "Michael Chen",
        role: "Frontend Developer",
        content: "The React course helped me land my dream job at a startup. Duke's teaching style made complex concepts easy to understand.",
        rating: 5
      }
    ],
    faq: [
      {
        question: "What JavaScript knowledge do I need?",
        answer: "You should be comfortable with ES6+ features, arrow functions, destructuring, and async/await. We'll review these concepts as needed."
      },
      {
        question: "Will I learn Redux?",
        answer: "Yes! We cover Redux Toolkit, the modern way to use Redux, along with alternative state management solutions."
      },
      {
        question: "Do you cover testing?",
        answer: "Absolutely! Testing is crucial for production apps. We'll cover Jest, React Testing Library, and testing best practices."
      }
    ]
  },
  {
    id: 3,
    title: "DevOps & Cloud",
    description: "Learn containerization, CI/CD, cloud platforms, and infrastructure as code.",
    duration: "10-14 weeks",
    level: "Intermediate to Advanced",
    price: "$35/hour",
    topics: ["Docker", "Kubernetes", "AWS/Azure", "CI/CD", "Terraform", "Monitoring"],
    icon: "☁️",
    color: "bg-purple-500",
    slug: "devops-cloud",
    detailedDescription: "Master modern DevOps practices and cloud technologies. Learn to deploy, scale, and maintain applications using industry-standard tools and platforms.",
    curriculum: [
      {
        week: "Weeks 1-2",
        title: "Containerization Fundamentals",
        topics: ["Docker basics and containers", "Docker images and registries", "Docker Compose", "Container networking", "Best practices"]
      },
      {
        week: "Weeks 3-4",
        title: "Kubernetes Orchestration",
        topics: ["Kubernetes architecture", "Pods, services, and deployments", "ConfigMaps and Secrets", "Ingress controllers", "Helm charts"]
      },
      {
        week: "Weeks 5-6",
        title: "Cloud Platforms (AWS/Azure)",
        topics: ["Cloud computing fundamentals", "AWS/Azure services", "Virtual machines and storage", "Load balancers", "Auto-scaling"]
      },
      {
        week: "Weeks 7-8",
        title: "CI/CD Pipelines",
        topics: ["Jenkins and GitHub Actions", "Pipeline automation", "Testing integration", "Deployment strategies", "Rollback procedures"]
      },
      {
        week: "Weeks 9-10",
        title: "Infrastructure as Code",
        topics: ["Terraform fundamentals", "Resource provisioning", "State management", "Modules and workspaces", "Best practices"]
      },
      {
        week: "Weeks 11-12",
        title: "Monitoring & Security",
        topics: ["Prometheus and Grafana", "Log aggregation", "Security scanning", "Compliance", "Disaster recovery"]
      },
      {
        week: "Weeks 13-14",
        title: "Advanced Topics & Projects",
        topics: ["Microservices architecture", "Service mesh", "GitOps", "Production deployment", "Portfolio projects"]
      }
    ],
    prerequisites: ["Basic Linux command line", "Understanding of web applications", "Basic networking knowledge"],
    outcomes: [
      "Deploy applications using Docker and Kubernetes",
      "Set up CI/CD pipelines for automated deployments",
      "Manage cloud infrastructure on AWS/Azure",
      "Implement Infrastructure as Code with Terraform",
      "Monitor and troubleshoot production systems",
      "Apply DevOps best practices and security measures",
      "Design scalable and resilient architectures"
    ],
    projects: [
      "Multi-tier application deployment",
      "CI/CD pipeline with automated testing",
      "Kubernetes cluster management",
      "Infrastructure automation with Terraform",
      "Monitoring and alerting system"
    ],
    testimonials: [
      {
        name: "Emily Rodriguez",
        role: "DevOps Engineer",
        content: "Duke's DevOps course gave me the confidence to work with Kubernetes and AWS. His real-world examples made complex concepts easy to understand.",
        rating: 5
      }
    ],
    faq: [
      {
        question: "Do I need cloud experience?",
        answer: "No prior cloud experience is required. We start with fundamentals and build up to advanced cloud concepts."
      },
      {
        question: "Which cloud platform do you focus on?",
        answer: "We cover both AWS and Azure, giving you flexibility to work with either platform or both."
      },
      {
        question: "Will I learn Kubernetes?",
        answer: "Yes! Kubernetes is a major focus of the course. You'll learn to deploy and manage containerized applications."
      }
    ]
  },
  {
    id: 4,
    title: "Machine Learning",
    description: "Introduction to ML algorithms, data preprocessing, model training, and deployment.",
    duration: "12-16 weeks",
    level: "Intermediate",
    price: "$32/hour",
    topics: ["Data Preprocessing", "Algorithms", "Model Training", "Evaluation", "Deployment", "Deep Learning"],
    icon: "🤖",
    color: "bg-orange-500",
    slug: "machine-learning",
    detailedDescription: "Dive deep into machine learning and artificial intelligence. Learn to build, train, and deploy ML models that solve real-world problems using Python and modern frameworks.",
    curriculum: [
      {
        week: "Weeks 1-2",
        title: "ML Fundamentals & Data Preprocessing",
        topics: ["Machine learning overview", "Data types and structures", "Data cleaning and preprocessing", "Feature engineering", "Data visualization"]
      },
      {
        week: "Weeks 3-4",
        title: "Supervised Learning Algorithms",
        topics: ["Linear and logistic regression", "Decision trees and random forests", "Support vector machines", "Naive Bayes", "Model evaluation metrics"]
      },
      {
        week: "Weeks 5-6",
        title: "Unsupervised Learning",
        topics: ["Clustering algorithms (K-means, hierarchical)", "Dimensionality reduction (PCA)", "Association rules", "Anomaly detection", "Evaluation methods"]
      },
      {
        week: "Weeks 7-8",
        title: "Ensemble Methods & Optimization",
        topics: ["Bagging and boosting", "Gradient boosting (XGBoost)", "Hyperparameter tuning", "Cross-validation", "Model selection"]
      },
      {
        week: "Weeks 9-10",
        title: "Deep Learning Fundamentals",
        topics: ["Neural networks basics", "Backpropagation", "Activation functions", "Optimization algorithms", "Regularization techniques"]
      },
      {
        week: "Weeks 11-12",
        title: "Advanced Deep Learning",
        topics: ["Convolutional Neural Networks (CNN)", "Recurrent Neural Networks (RNN)", "LSTM and GRU", "Transfer learning", "Computer vision applications"]
      },
      {
        week: "Weeks 13-14",
        title: "Model Deployment & Production",
        topics: ["Model serialization", "API development", "Containerization", "Cloud deployment", "Model monitoring"]
      },
      {
        week: "Weeks 15-16",
        title: "Specialized Applications",
        topics: ["Natural Language Processing", "Time series analysis", "Recommendation systems", "Portfolio projects", "Career guidance"]
      }
    ],
    prerequisites: ["Strong Python programming skills", "Basic statistics knowledge", "Understanding of linear algebra", "Pandas and NumPy familiarity"],
    outcomes: [
      "Build and train machine learning models from scratch",
      "Preprocess and engineer features for ML applications",
      "Implement supervised and unsupervised learning algorithms",
      "Develop deep learning models using TensorFlow/PyTorch",
      "Deploy ML models to production environments",
      "Evaluate and optimize model performance",
      "Apply ML to real-world business problems"
    ],
    projects: [
      "Image classification system",
      "Sentiment analysis application",
      "Customer segmentation model",
      "Stock price prediction system",
      "Recommendation engine"
    ],
    testimonials: [
      {
        name: "David Kim",
        role: "ML Engineer",
        content: "The machine learning course was comprehensive and well-structured. Duke's guidance helped me build my first ML model and deploy it successfully.",
        rating: 5
      }
    ],
    faq: [
      {
        question: "What math background do I need?",
        answer: "Basic knowledge of statistics, linear algebra, and calculus is helpful but not required. We'll cover the necessary mathematical concepts."
      },
      {
        question: "Which ML frameworks will I learn?",
        answer: "We cover scikit-learn, TensorFlow, and PyTorch, giving you experience with the most popular ML frameworks."
      },
      {
        question: "Will I learn deep learning?",
        answer: "Yes! Deep learning is a major part of the course, including CNNs, RNNs, and modern architectures."
      }
    ]
  },
  {
    id: 5,
    title: "Data Analysis",
    description: "Analyze data using Python, pandas, visualization tools, and statistical methods.",
    duration: "6-8 weeks",
    level: "Beginner to Intermediate",
    price: "$28/hour",
    topics: ["Pandas", "NumPy", "Matplotlib", "Seaborn", "Statistics", "SQL"],
    icon: "📊",
    color: "bg-indigo-500",
    slug: "data-analysis",
    detailedDescription: "Master data analysis with Python. Learn to extract insights from data using powerful libraries and statistical methods. Perfect for business analysts, researchers, and data enthusiasts.",
    curriculum: [
      {
        week: "Weeks 1-2",
        title: "Python Data Analysis Fundamentals",
        topics: ["NumPy arrays and operations", "Pandas DataFrames", "Data loading and cleaning", "Basic data manipulation", "Data types and structures"]
      },
      {
        week: "Weeks 3-4",
        title: "Data Visualization",
        topics: ["Matplotlib basics", "Seaborn statistical plots", "Customizing visualizations", "Interactive plots with Plotly", "Dashboard creation"]
      },
      {
        week: "Weeks 5-6",
        title: "Statistical Analysis",
        topics: ["Descriptive statistics", "Hypothesis testing", "Correlation analysis", "Regression analysis", "Statistical significance"]
      },
      {
        week: "Weeks 7-8",
        title: "Advanced Analysis & SQL",
        topics: ["Time series analysis", "SQL for data analysis", "Database connections", "Advanced pandas techniques", "Portfolio projects"]
      }
    ],
    prerequisites: ["Basic Python knowledge", "Understanding of basic statistics", "No prior data analysis experience required"],
    outcomes: [
      "Clean and preprocess data effectively",
      "Create compelling data visualizations",
      "Perform statistical analysis and hypothesis testing",
      "Work with time series data",
      "Query databases using SQL",
      "Generate actionable insights from data",
      "Present findings in professional reports"
    ],
    projects: [
      "Sales performance analysis dashboard",
      "Customer behavior analysis",
      "Financial data trend analysis",
      "A/B testing results evaluation",
      "Market research data insights"
    ],
    testimonials: [
      {
        name: "Lisa Wang",
        role: "Business Analyst",
        content: "Duke's data analysis course helped me automate my reporting tasks and gain insights I never knew were possible. Highly recommended!",
        rating: 5
      }
    ],
    faq: [
      {
        question: "Do I need statistics knowledge?",
        answer: "Basic statistics knowledge is helpful but not required. We'll cover the necessary statistical concepts as we go."
      },
      {
        question: "What datasets will I work with?",
        answer: "You'll work with real-world datasets including sales data, customer data, financial data, and more."
      },
      {
        question: "Will I learn SQL?",
        answer: "Yes! SQL is essential for data analysis. We'll cover database queries and data extraction techniques."
      }
    ]
  },
  {
    id: 6,
    title: "Web Development",
    description: "Full-stack web development with HTML, CSS, JavaScript, and modern frameworks.",
    duration: "8-12 weeks",
    level: "Beginner",
    price: "$22/hour",
    topics: ["HTML/CSS", "JavaScript", "Node.js", "Databases", "APIs", "Deployment"],
    icon: "🌐",
    color: "bg-teal-500",
    slug: "web-development",
    detailedDescription: "Learn full-stack web development from scratch. Build modern, responsive websites and web applications using industry-standard technologies and best practices.",
    curriculum: [
      {
        week: "Weeks 1-2",
        title: "Frontend Fundamentals",
        topics: ["HTML5 semantic elements", "CSS3 styling and layout", "Responsive design", "Flexbox and Grid", "CSS animations"]
      },
      {
        week: "Weeks 3-4",
        title: "JavaScript Essentials",
        topics: ["JavaScript fundamentals", "DOM manipulation", "Event handling", "ES6+ features", "Async programming"]
      },
      {
        week: "Weeks 5-6",
        title: "Frontend Frameworks",
        topics: ["React basics", "Components and props", "State management", "Routing", "API integration"]
      },
      {
        week: "Weeks 7-8",
        title: "Backend Development",
        topics: ["Node.js fundamentals", "Express.js framework", "RESTful APIs", "Middleware", "Authentication"]
      },
      {
        week: "Weeks 9-10",
        title: "Database Integration",
        topics: ["SQL databases", "MongoDB NoSQL", "Database design", "ORM/ODM tools", "Data relationships"]
      },
      {
        week: "Weeks 11-12",
        title: "Deployment & Production",
        topics: ["Version control with Git", "Deployment strategies", "Cloud platforms", "Performance optimization", "Security best practices"]
      }
    ],
    prerequisites: ["Basic computer skills", "No prior programming experience required", "Willingness to learn and practice"],
    outcomes: [
      "Build responsive websites using HTML, CSS, and JavaScript",
      "Develop interactive web applications with React",
      "Create backend APIs using Node.js and Express",
      "Work with both SQL and NoSQL databases",
      "Deploy applications to cloud platforms",
      "Implement authentication and security features",
      "Use version control and collaboration tools"
    ],
    projects: [
      "Personal portfolio website",
      "E-commerce web application",
      "Task management system",
      "Social media dashboard",
      "Real-time chat application"
    ],
    testimonials: [
      {
        name: "Alex Thompson",
        role: "Frontend Developer",
        content: "Starting from zero, Duke's web development course gave me all the skills I needed to build professional websites. The hands-on approach was perfect.",
        rating: 5
      }
    ],
    faq: [
      {
        question: "Do I need any programming experience?",
        answer: "No! This course is designed for complete beginners. We start with the basics and build up your skills progressively."
      },
      {
        question: "Will I learn both frontend and backend?",
        answer: "Yes! This is a full-stack course covering both frontend (HTML, CSS, JavaScript, React) and backend (Node.js, databases) development."
      },
      {
        question: "What projects will I build?",
        answer: "You'll build 5 real-world projects including a portfolio website, e-commerce app, and a full-stack web application."
      }
    ]
  }
];

export const courseCategories = [
  {
    name: "Programming",
    courses: [1, 6],
    description: "Learn to code from scratch or advance your programming skills"
  },
  {
    name: "Web Development",
    courses: [2, 6],
    description: "Build modern, responsive web applications"
  },
  {
    name: "Data Science",
    courses: [4, 5],
    description: "Analyze data and build intelligent systems"
  },
  {
    name: "DevOps",
    courses: [3],
    description: "Deploy and manage applications in the cloud"
  }
];
